import type { Grid as GridConfig, ResolvedWidget } from '@hud/core';
import { getWidget } from '../registry.js';
import { Card } from './Card.js';
import { ErrorTile } from './ErrorTile.js';
import { WidgetBoundary } from './WidgetBoundary.js';

export interface GridProps {
  grid: GridConfig;
  widgets: ResolvedWidget[];
}

/**
 * Renders the dashboard layout.
 *
 * Placement comes straight from markdown, so this is plain CSS Grid with
 * explicit line placement — no grid library, no measurement, no layout state.
 * That's the payoff of choosing markdown-defined layout over drag-and-drop.
 */
export function Grid({ grid, widgets }: GridProps) {
  const style: Record<string, string> = {
    '--hud-grid-columns': String(grid.columns),
    '--hud-grid-row-height': `${grid.rowHeight}px`,
    '--hud-grid-gap': `${grid.gap}px`,
  };
  if (grid.maxWidth) style['--hud-grid-max-width'] = `${grid.maxWidth}px`;

  return (
    <div class="hud-grid" style={style}>
      {widgets.map((widget) => (
        <div
          key={`${widget.id}-${widget.placement.x}-${widget.placement.y}`}
          class="hud-grid__cell"
          style={{
            gridColumn: `${widget.placement.x + 1} / span ${widget.placement.w}`,
            gridRow: `${widget.placement.y + 1} / span ${widget.placement.h}`,
          }}
        >
          <WidgetCell widget={widget} />
        </div>
      ))}
    </div>
  );
}

function WidgetCell({ widget }: { widget: ResolvedWidget }) {
  if (!widget.ok) {
    return <ErrorTile path={widget.path} message={widget.error} />;
  }

  const def = getWidget(widget.type);
  if (!def) {
    // Unreachable via the engine (it validates against this same registry), but
    // cheap insurance against a registry mutated after load.
    return <ErrorTile path={widget.path} message={`No renderer for widget type "${widget.type}"`} />;
  }

  const config = widget.config as { title?: string; bare?: boolean };
  const Widget = def.component;

  return (
    <WidgetBoundary path={widget.path}>
      <Card title={config.title} bare={config.bare ?? false}>
        <Widget config={widget.config as never} />
      </Card>
    </WidgetBoundary>
  );
}
